/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Dispatch, FC, createContext, useReducer } from "react";
import { Announcement } from "services/announcementService";
import {
  addToList,
  addToListById,
  removeFromList,
  removeFromListById,
} from "services/helperService";
import { SupademoDemoDto } from "services/supademosService";
import { Socket } from "socket.io-client";

type Action = {
  type:
    | "INITIAL_DATA_FETCHED"
    | "SET_LOADING_STATE"
    | "SET_SOCKET_HANDLER"
    | "SET_CURRENT_USER"
    | "SET_AUTH_TOKEN"
    | "SET_UNREAD_CHATS"
    | "INC_UNREAD_CHATS"
    | "DEC_UNREAD_CHATS"
    | "SET_NEW_ANNOUNCEMENTS"
    | "INC_NEW_ANNOUNCEMENTS"
    | "DEC_NEW_ANNOUNCEMENTS"
    | "UPDATE_ACTIVE_FEATURES"
    | "SET_ACTIVE_FEATURES"
    | "UPDATE_CONFIG"
    | "UPDATE_RELEASE_DATES"
    | "SET_SUBSCRIPTION_INFO"
    | "SET_NEXT_RELEASE_DATE"
    | "SET_PROFILE"
    | "SET_REFERRAL_STATUS"
    | "SET_PENDING_REFERRAL_STATUS"
    | "SET_REDEMPTION_HISTORY"
    | "SET_ANNOUNCEMENTS"
    | "SET_MESSAGE_NOTIFICATIONS"
    | "APPEND_MESSAGE_NOTIFICATIONS"
    | "REMOVE_MESSAGE_NOTIFICATION"
    | "APPEND_ANNOUNCEMENTS"
    | "UPDATE_ANNOUNCEMENTS_READ_STATUS"
    | "ADD_ANNOUNCEMENT"
    | "CLEAR_ANNOUNCEMENTS"
    | "SET_CHAT_CONVERSATIONS"
    | "SET_CHAT_MESSAGES"
    | "SET_CURRENT_CONVERSATION"
    | "ADD_CHAT_MESSAGE"
    | "UPDATE_MESSAGES_SEEN_STATUS"
    | "SET_CHAT_LOADING"
    | "SET_USER_PRODUCTS"
    | "SET_DELEGATE_USER"
    | "SET_IMPERSONATING"
    | "BUMP_ANNOUNCEMENTS_VERSION"
    | "SET_SUPADEMOS";
  payload: LooseObject;
};

export interface SubscriptionPeriodInfo {
  start_date?: number;
  current_period_start?: number;
  current_period_end?: number;
  interval?: string;
  isFreeTrial?: boolean;
  freeTrialEnd?: number;
}

interface ReferralStatus {
  totalReferrals?: number;
  firstCredit?: number;
  partialCredit?: number;
  referredOffices?: number;
  totalPoints?: number;
  oncologyOffices?: number;
}
export interface iUserProducts {
  id?: number;
  UserId?: number;
  ProductId?: number;
  ProductName?: string;
  URL?: string;
  GenericName?: string;
  FormulationType?: string;
}
export interface ChatMessage {
  id: number;
  chatId: number;
  senderId: number;
  senderName: string | null;
  message: string | null;
  time: string;
  isSeen?: boolean | number | undefined;
  isDelivered?: boolean | number | undefined;
}

export interface ChatConversation {
  id: number;
  initiatedBy: number;
  latestMessageSenderId: number | null;
  latestMessage: string | null;
  latestMessageSeen: boolean;
  latestMessageTime: string;
  notifiedByEmail: boolean;
  firstName?: string;
  lastName?: string;
  userId?: string;
  photo?: string;
}

interface UserContextState {
  socketHandler: null | Socket;
  currentUser: LooseObject;
  delegateUser: LooseObject;
  authToken: string;
  unreadChats: number[];
  newAnnouncements: LooseObject[];
  activeFeatures: string[];
  config: JObj[];
  releaseDates: JObj[];
  profile: LooseObject;
  initialDataFetching: boolean;
  isLoading: boolean;
  subscriptionInfo?: SubscriptionPeriodInfo;
  nextReleaseDate?: string | null | undefined;
  referralStatus?: ReferralStatus;
  pendingReferralStatus?: JObj[];
  redemptionHistory?: JObj[];
  announcements: Announcement[];
  messageNotifications: JObj[] | [];
  lastAnnouncementFetchTime: number;
  lastMessagesFetchTime: number;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  } | null;
  chat: {
    conversations: ChatConversation[];
    messages: ChatMessage[];
    currentConversationId: number | null;
    isLoading: boolean;
  };
  userProducts: iUserProducts[];
  isImpersonating?: boolean;
  unreadAnnouncementsCount: number;
  announcementsVersion: number;
  lastSupademosFetchTime: number;
  supademos: SupademoDemoDto[];
}

interface ContextType {
  state: UserContextState;
  dispatch: Dispatch<Action>;
}

const initialState: UserContextState = {
  socketHandler: null,
  currentUser: {},
  delegateUser: {},
  authToken: "",
  unreadChats: [],
  newAnnouncements: [],
  activeFeatures: [],
  config: [],
  releaseDates: [],
  profile: {},
  initialDataFetching: true,
  isLoading: false,
  nextReleaseDate: null,
  pendingReferralStatus: [],
  redemptionHistory: [],
  announcements: [],
  messageNotifications: [],
  lastAnnouncementFetchTime: 0,
  lastMessagesFetchTime: 0,
  pagination: null,
  chat: {
    conversations: [],
    messages: [],
    currentConversationId: null,
    isLoading: false,
  },
  userProducts: [],
  isImpersonating: false,
  unreadAnnouncementsCount: 0,
  announcementsVersion: 0,
  lastSupademosFetchTime: 0,
  supademos: [],
};

export const UserContext = createContext<ContextType | undefined>(undefined);

const reducer = (state: UserContextState, action: Action) => {
  switch (action.type) {
    case "INITIAL_DATA_FETCHED":
      return { ...state, initialDataFetching: false };
    case "SET_LOADING_STATE":
      return { ...state, isLoading: action.payload.isLoading };
    case "SET_IMPERSONATING":
      return { ...state, isImpersonating: action.payload.isImpersonating };
    case "SET_SOCKET_HANDLER":
      return { ...state, socketHandler: action.payload.socketHandler };
    case "SET_CURRENT_USER":
      return { ...state, currentUser: action.payload.currentUser };
    case "SET_DELEGATE_USER":
      return { ...state, delegateUser: action.payload.delegateUser };
    case "SET_AUTH_TOKEN":
      return { ...state, authToken: action.payload.authToken };
    case "SET_NEXT_RELEASE_DATE":
      return { ...state, nextReleaseDate: action.payload.nextReleaseDate };
    case "UPDATE_ACTIVE_FEATURES":
      return {
        ...state,
        activeFeatures: addToList(
          [...state.activeFeatures],
          action.payload.feature,
        ),
      };
    case "SET_ACTIVE_FEATURES":
      return {
        ...state,
        activeFeatures: action.payload.features,
      };
    case "UPDATE_CONFIG":
      return {
        ...state,
        config: [...state.config, ...action.payload.config],
      };
    case "UPDATE_RELEASE_DATES":
      return {
        ...state,
        releaseDates: [...state.releaseDates, ...action.payload.releaseDates],
      };
    case "SET_PROFILE":
      return {
        ...state,
        profile: action.payload.profile,
      };
    case "SET_UNREAD_CHATS":
      return {
        ...state,
        unreadChats: action.payload.unreadChats,
      };
    case "INC_UNREAD_CHATS":
      return {
        ...state,
        unreadChats: addToList(
          [...state.unreadChats],
          action.payload.unreadChatId,
        ),
      };
    case "DEC_UNREAD_CHATS":
      return {
        ...state,
        unreadChats: removeFromList(
          [...state.unreadChats],
          action.payload.unreadChatId,
        ),
      };
    case "SET_NEW_ANNOUNCEMENTS":
      return {
        ...state,
        newAnnouncements: action.payload.newAnnouncements,
      };

    case "INC_NEW_ANNOUNCEMENTS":
      return {
        ...state,
        newAnnouncements: addToListById(
          [...state.newAnnouncements],
          action.payload.newAnnouncement,
        ),
      };
    case "DEC_NEW_ANNOUNCEMENTS":
      return {
        ...state,
        newAnnouncements: removeFromListById(
          [...state.newAnnouncements],
          action.payload.announcement,
        ),
      };
    case "SET_SUBSCRIPTION_INFO":
      return {
        ...state,
        subscriptionInfo: action.payload.subscriptionInfo,
      };
    case "SET_REFERRAL_STATUS":
      return { ...state, referralStatus: action.payload.referralStatus };
    case "SET_PENDING_REFERRAL_STATUS":
      return {
        ...state,
        pendingReferralStatus: action.payload.pendingReferralStatus,
      };
    case "SET_REDEMPTION_HISTORY":
      return {
        ...state,
        redemptionHistory: action.payload.redemptionHistory,
      };
    case "SET_MESSAGE_NOTIFICATIONS":
      return {
        ...state,
        messageNotifications: action.payload.messageNotifications,
        lastMessagesFetchTime:
          action.payload.lastMessagesFetchTime || Date.now(),
      };
    case "APPEND_MESSAGE_NOTIFICATIONS":
      return {
        ...state,
        messageNotifications: state.messageNotifications.some(
          (notification) =>
            notification.chatId === action.payload.messageNotification.chatId,
        )
          ? state.messageNotifications.map((notification) =>
              notification.chatId === action.payload.messageNotification.chatId
                ? action.payload.messageNotification
                : notification,
            )
          : [
              ...(state.messageNotifications || []),
              action.payload.messageNotification,
            ],
      };
    case "REMOVE_MESSAGE_NOTIFICATION":
      return {
        ...state,
        messageNotifications: state.messageNotifications.filter(
          (notification) => notification.chatId !== action.payload.chatId,
        ),
      };
    case "SET_ANNOUNCEMENTS":
      return {
        ...state,
        announcements: action.payload.announcements,
        unreadAnnouncementsCount: Number(action.payload.unreadCount ?? 0),
        lastAnnouncementFetchTime: Date.now(),
        pagination: action.payload.pagination,
      };

    case "UPDATE_ANNOUNCEMENTS_READ_STATUS": {
      const ids: number[] = Array.isArray(action.payload.announcementIds)
        ? action.payload.announcementIds
        : [];

      let newlyRead = 0;

      const updatedAnnouncements = state.announcements.map((a: any) => {
        if (!ids.includes(a.id)) return a;
        if (a.isReadByMe === 1) return a;
        newlyRead += 1;
        return { ...a, isReadByMe: 1 };
      });

      return {
        ...state,
        announcements: updatedAnnouncements,
        unreadAnnouncementsCount: Math.max(
          0,
          (state.unreadAnnouncementsCount || 0) - newlyRead,
        ),
      };
    }

    case "ADD_ANNOUNCEMENT": {
      const incoming = action.payload.announcement || {};
      const normalized = {
        ...incoming,
        isReadByMe: incoming.isReadByMe ?? 0,
      };

      return {
        ...state,
        announcements: [normalized, ...(state.announcements || [])],
        unreadAnnouncementsCount: (state.unreadAnnouncementsCount || 0) + 1,
      };
    }

    case "CLEAR_ANNOUNCEMENTS":
      return {
        ...state,
        announcements: [],
        pagination: undefined,
      };
    case "SET_CHAT_CONVERSATIONS":
      return {
        ...state,
        chat: {
          ...state.chat,
          conversations: action.payload.conversations,
        },
      };
    case "SET_CHAT_MESSAGES":
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: action.payload.messages,
        },
      };
    case "SET_CURRENT_CONVERSATION":
      return {
        ...state,
        chat: {
          ...state.chat,
          currentConversationId: action.payload.conversationId,
        },
      };
    case "ADD_CHAT_MESSAGE": {
      // Chat and MessagesPanel both listen for incoming socket messages, so
      // the same message can be dispatched more than once. Messages from the
      // server always carry an id — drop repeats. (Optimistic sends have no
      // id yet and are always appended.)
      const incomingMessage = action.payload.message;
      if (
        incomingMessage?.id != null &&
        state.chat.messages.some((m) => m.id === incomingMessage.id)
      ) {
        return state;
      }
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: [...state.chat.messages, incomingMessage],
        },
      };
    }
    case "SET_CHAT_LOADING":
      return {
        ...state,
        chat: {
          ...state.chat,
          isLoading: action.payload.isLoading,
        },
      };
    case "UPDATE_MESSAGES_SEEN_STATUS":
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: state.chat.messages.map((message) =>
            message.chatId === action.payload.chatId
              ? // && action.payload.messageIds.includes(message.id)
                { ...message, isSeen: true }
              : message,
          ),
        },
      };
    case "SET_USER_PRODUCTS":
      return {
        ...state,
        userProducts: action.payload.userProducts,
      };
    case "BUMP_ANNOUNCEMENTS_VERSION":
      return {
        ...state,
        announcementsVersion: Number(state.announcementsVersion || 0) + 1,
      };
    case "SET_SUPADEMOS":
      return {
        ...state,
        supademos: action.payload.supademos || [],
        lastSupademosFetchTime:
          action.payload.lastSupademosFetchTime || Date.now(),
      };
    default:
      return state;
  }
};

export const UserContextProvider: FC<{ children: any }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <UserContext.Provider value={{ state, dispatch }}>
      {children}
    </UserContext.Provider>
  );
};
